import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { initAnalytics } from "../../lib/analytics";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import { modeIcons, statusColors } from "../../lib/constants";
import type { Order } from "../../lib/types";
import { formatDate } from "../../lib/utils";
import { COMMANDS, PHARMACY_COMMANDS, COMING_SOON_COMMANDS, generateDynamicPharmacyCommands } from "../../services/mockDataService";
import type { Entity } from "../../lib/types";
import { Filters } from "../ui/Filters";
import { StatusCard } from "../ui/StatusCard";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { OrderDrawer } from "./OrderDrawer";
import { useToast } from "../../hooks/use-toast";
import Pagination from "../ui/pagination";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Download } from "lucide-react";


export const OrderTable: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const navigate = useNavigate();
  const { accessLevel, user, userEntities, excelPermissions, userRole } = useAuth();
  const [searchParams] = useSearchParams();
  const selectedOrderId = searchParams.get("orderId");
  const selectedStartDate = searchParams.get("startDate");
  const selectedEndDate = searchParams.get("endDate");
  const selectedStatus = searchParams.get("status");
  const urlEntity = searchParams.get("entity");
  const urlState = searchParams.get("state");
  const urlMode = searchParams.get("mode");
  const urlProvider = searchParams.get("provider");
  const { toast } = useToast();

  const [startDate, setStartDate] = useState<string>(
    selectedStartDate ?? new Date().toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState<string>(
    selectedEndDate ?? new Date().toISOString().split("T")[0],
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchFilter, setSearchFilter] = useState<string>(
    searchParams.get("search") ?? "",
  );
  const [groupedOrders, setGroupedOrders] = useState<
    { status: string; count: number }[]
  >([]);
  const [statusFilter, setStatusFilter] = useState<string>(
    selectedStatus ?? "",
  );
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [mixpanelInstance, setMixpanelInstance] = useState<any>(null);
  const [selectedState, setSelectedState] = useState<string>(urlState ?? "all");
  const [selectedProvider, setSelectedProvider] = useState<number>(
    urlProvider ? Number(urlProvider) : 0,
  );
  const [selectedMode, setSelectedMode] = useState<string>(urlMode ?? "all");
  const [selectedEntity, setSelectedEntity] = useState<number>(
    urlEntity ? Number(urlEntity) : 0,
  );
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const [dynamicPharmacyCommands, setDynamicPharmacyCommands] = useState<typeof PHARMACY_COMMANDS>([]);
  const [entities, setEntities] = useState<{value: number; label: string}[]>([]);
  const [limit, setLimit] = useState(10);
  const [availableStates, setAvailableStates] = useState<string[]>([]);

  useEffect(() => {
    const mp = initAnalytics();
    if (mp && user) {
      mp.identify(user.id);
      mp.people.set({
        $email: user.email,
      });
      setMixpanelInstance(mp);
    }
  }, [user]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsCommandOpen(open => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    const startParam = searchParams.get("startDate");
    const endParam = searchParams.get("endDate");
    const statusParam = searchParams.get("status");
    const entityParam = searchParams.get("entity");
    const stateParam = searchParams.get("state");
    const modeParam = searchParams.get("mode");
    const providerParam = searchParams.get("provider");

    if (startParam) {
      setStartDate(startParam);
    }
    if (endParam) {
      setEndDate(endParam);
    }

    // Always sync status filter with URL (clear if not present)
    setStatusFilter(statusParam || "");

    // Always sync entity with URL (reset to 0 if not present)
    setSelectedEntity(entityParam ? Number(entityParam) : 0);

    // Always sync state with URL (reset to "all" if not present)
    setSelectedState(stateParam || "all");

    // Always sync mode with URL (reset to "all" if not present)
    setSelectedMode(modeParam || "all");

    // Always sync provider with URL (reset to 0 if not present)
    setSelectedProvider(providerParam ? Number(providerParam) : 0);
  }, [searchParams]);

  const fetchOrders = async () => {
    try {
      const isSuperAdmin = userRole?.role_name === "Super Admin";
      const hasProviderAccess =
        accessLevel?.access_level?.provider_id &&
        accessLevel.access_level.provider_id.length > 0;

      const allowedEntityIds = hasProviderAccess
        ? []
        : userEntities && userEntities.length > 0
          ? userEntities.map(e => e.entity_id)
          : accessLevel?.entities?.map(id => Number(id)) || [];

      const hasSearch = searchFilter.trim().length > 0;
      const result = await api.getOrders({
        start_date: hasSearch ? undefined : startDate,
        end_date: hasSearch ? undefined : endDate,
        entities: allowedEntityIds.length > 0 && allowedEntityIds.length < 500 ? allowedEntityIds.join(",") : undefined,
        providers: selectedProvider.toString(),
        mode: selectedMode,
        page: currentPage,
        status: statusFilter,
        keyword: searchFilter,
        state: selectedState,
        entity: selectedEntity,
        limit: limit,
        user_id: user?.id || "",
      });
      setOrders(result.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    }
  };

  const fetchGroupedOrders = async () => {
    const isSuperAdmin = userRole?.role_name === "Super Admin";
    const hasProviderAccess =
      accessLevel?.access_level?.provider_id &&
      accessLevel.access_level.provider_id.length > 0;

    const allowedEntityIds = hasProviderAccess
      ? []
      : userEntities && userEntities.length > 0
        ? userEntities.map(e => e.entity_id)
        : accessLevel?.entities?.map(id => Number(id)) || [];

    const result = await api.getGroupedOrders({
      start_date: startDate,
      end_date: endDate,
      mode: selectedMode,
      providers: selectedProvider.toString(),
      state: selectedState,
      entity: selectedEntity,
      entities: allowedEntityIds.length > 0 && allowedEntityIds.length < 500 ? allowedEntityIds.join(",") : undefined,
    });
    setGroupedOrders(result.data);
  };

  useEffect(() => {
    setTotalOrders(
      groupedOrders.reduce((acc, curr) => acc + Number(curr.count), 0),
    );
  }, [groupedOrders]);

  const fetchEntitiesAndCommands = async () => {
    try {
      const isSuperAdmin = userRole?.role_name === "Super Admin";
      let allResults: any[] = [];

      if (userEntities && userEntities.length > 0) {
        // userEntities now includes ALL entities (active + inactive) for Super Admin
        allResults = userEntities.map((e: any) => ({
          id: e.entity_id,
          name: e.name,
          branch_id: e.branch_id,
          is_active: e.is_active !== false, // default true if not set
        }));
      }

      const formattedEntities: { value: number; label: string }[] = [];
      allResults.forEach((entity: any) => {
        // Super Admin sees all entities; others only see active ones
        if (!isSuperAdmin && !entity.is_active) return;

        formattedEntities.push({
          value: Number(entity.id),
          label: isSuperAdmin && entity.is_active === false
            ? `${entity.name} (${entity.branch_id}) [Inactive]`
            : `${entity.name} (${entity.branch_id})`,
        });
      });

      setEntities([
        { value: 0, label: "All Pharmacies" },
        ...formattedEntities,
      ]);
      // dynamic pharmacy commands for the command palette
      const dynamicCommands = generateDynamicPharmacyCommands(allResults);
      setDynamicPharmacyCommands(dynamicCommands);
    } catch (error) {
      console.error("Failed to fetch entities:", error);
      // Fallback to static commands
      setDynamicPharmacyCommands(PHARMACY_COMMANDS);
    }
  };

    useEffect(() => {
    fetchEntitiesAndCommands();
  }, []);

   // Refresh entities when command dialog opens (ensures fresh data)
  useEffect(() => {
    if (isCommandOpen) {
      fetchEntitiesAndCommands();
    }
  }, [isCommandOpen]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const fetchedData = await api.getFilters();
        if (fetchedData?.data?.states) {
          const stateNames = fetchedData.data.states.map((state: any) => state.state);
          setAvailableStates(stateNames);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };

    fetchStates();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchOrders and fetchGroupedOrders are not dependencies
  useEffect(() => {
    if (startDate && endDate && currentPage) {
      const fetchData = async () => {
        await fetchOrders();
      };
      fetchData();
    }
  }, [
    startDate,
    endDate,
    selectedMode,
    currentPage,
    selectedProvider,
    selectedState,
    selectedEntity,
    limit,
  ]);

  useEffect(() => {
  if (startDate && endDate && selectedMode) {
    fetchGroupedOrders();
  }
}, [
  startDate,
  endDate,
  selectedMode,
  selectedProvider,
  selectedState,
  selectedEntity,
]);

  const updateSearchParams = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set(key, value);
    // Clear status parameter when other filters are changed, but preserve it for pagination and order selection
    // Keep status when key is one of: status, orderId, page, limit
    if (!["status", "orderId", "page", "limit"].includes(key)) {
      params.delete("status");
    }
    if (mixpanelInstance) {
      mixpanelInstance.track("search_orders", {
        search_filter: searchFilter,
        status_filter: statusFilter,
        start_date: startDate,
        end_date: endDate,
        order_id: selectedOrderId,
      });
    }
    navigate(`/orders?${params.toString()}`);
  };

  useEffect(() => {
    if (searchFilter === "" || searchFilter.length > 2) {
      fetchOrders();
      if (statusFilter === "") {
        fetchGroupedOrders();
      }
    }
    if (mixpanelInstance) {
      mixpanelInstance.track("filtered_orders", {
        search_filter: searchFilter,
        status_filter: statusFilter,
      });
    }
  }, [mixpanelInstance, statusFilter, searchFilter]);

  const calculateTAT = (tat: number) => {
    if (tat > 1440) {
      // 1 day
      const days = Math.floor(tat / 1440);
      const hours = Math.floor((tat % 1440) / 60);
      return (
        <Badge
          variant="outline"
          className="bg-red-600 text-white dark:bg-red-400 dark:text-black"
        >
          {days}d {hours}h
        </Badge>
      );
    }
    if (tat > 60) {
      // 1 hour
      const hours = Math.floor(tat / 60);
      const minutes = tat % 60;
      return (
        <Badge
          variant="outline"
          className="bg-red-400 text-white dark:bg-red-200 dark:text-black"
        >
          {hours}h {minutes}m
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="bg-green-600 text-white dark:bg-green-400 dark:text-black"
      >
        {tat}m
      </Badge>
    );
  };

  const resetSearchParams = () => {
    const url = new URL(window.location.href);
    url.searchParams.forEach((_, key) => {
      url.searchParams.delete(key);
    });
    navigate("/orders");
  };



  const downloadExcel = async () => {
    try {
      toast({
        title: "Exporting orders...",
        description: "Please wait for the file to download",
        duration: 4000,
        variant: "default",
      });
      const isSuperAdmin = userRole?.role_name === "Super Admin";
      const hasProviderAccess =
        accessLevel?.access_level?.provider_id &&
        accessLevel.access_level.provider_id.length > 0;

      const allowedEntityIds = hasProviderAccess
        ? []
        : userEntities && userEntities.length > 0
          ? userEntities.map((e: any) => e.entity_id)
          : accessLevel?.entities?.map((id: string | number) => Number(id)) || [];

      const response = await api.getOrdersExcelFile({
        start_date: startDate,
        end_date: endDate,
        mode: selectedMode,
        status: statusFilter,
        providers: selectedProvider !== 0 ? selectedProvider.toString() : undefined,
        entity: selectedEntity,
        entities: allowedEntityIds.length > 0 && allowedEntityIds.length < 500 ? allowedEntityIds.join(",") : undefined,
        user_id: user?.id || "",
      });

      if (response.success) {
        // Convert base64 to blob
        const content = response.data.content;
        const binary = atob(content);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = response.data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Failed to download:", response.error);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleDrawerClose = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("orderId");
    navigate(`/orders?${params.toString()}`);
    fetchOrders();
    fetchGroupedOrders();
  };

  return (
    <div className="space-y-4 min-h-screen bg-white dark:bg-gray-900 p-6">
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <h1 className="text-2xl font-bold dark:text-white">Orders</h1>
        <Filters
            selectedState={selectedState}
            setSelectedState={current_state => {
              setSelectedState(current_state);
              setStatusFilter(""); // Clear status filter when state changes
              updateSearchParams("state", current_state);
            }}
            selectedProvider={Number(selectedProvider)}
            setSelectedProvider={provider => {
              setSelectedProvider(provider);
              setStatusFilter(""); // Clear status filter when provider changes
              updateSearchParams("provider", provider.toString());
            }}
            selectedEntity={selectedEntity}
            setSelectedEntity={entity => {
              setSelectedEntity(entity);
              setStatusFilter(""); // Clear status filter when entity changes
              updateSearchParams("entity", entity.toString());
            }}
            selectedMode={selectedMode}
            setSelectedMode={mode => {
              setSelectedMode(mode);
              setStatusFilter(""); // Clear status filter when mode changes
              updateSearchParams("mode", mode);
            }}
            startDate={startDate}
            setStartDate={date => {
              setStartDate(date);
              setStatusFilter(""); // Clear status filter when start date changes
              updateSearchParams("startDate", date);
            }}
            endDate={endDate}
            setEndDate={date => {
              setEndDate(date);
              setStatusFilter(""); // Clear status filter when end date changes
              updateSearchParams("endDate", date);
            }}
            hidePharmacyFilter={
              accessLevel?.access_level?.provider_id &&
              accessLevel.access_level.provider_id.length > 0
            }
          />
      </div>
      <div className="grid grid-cols-4 gap-4">
        <StatusCard
          status="Total Orders"
          count={groupedOrders.reduce(
            (acc, curr) => acc + Number(curr.count),
            0,
          )}
          isSelected={false}
          onClick={() => {}}
        />
        {groupedOrders.map((order: { status: string; count: number }) => (
          <StatusCard
            key={order.status}
            status={order.status}
            count={order.count}
            isSelected={statusFilter === order.status}
            onClick={() => {
              // Preserve existing filters when status is selected
              setStatusFilter(order.status);
              setCurrentPage(1);
              setTotalOrders(order.count);

              // Update URL to include status filter while preserving other filters
              const params = new URLSearchParams(searchParams);
              params.set("status", order.status);
              params.set("startDate", startDate);
              params.set("endDate", endDate);
              params.set("page", "1"); // Reset to first page
              navigate(`/orders?${params.toString()}`);
            }}
          />
        ))}
      </div>

      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          <Input
            type="text"
            placeholder="Search Order"
            className="w-64 dark:bg-gray-800 dark:text-white dark:border-gray-700"
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
          />
          <Button
            variant="outline"
            className="dark:bg-gray-800 dark:text-white dark:border-gray-700 dark:hover:bg-gray-700"
            onClick={() => {
              setStatusFilter("");
              setSearchFilter("");
              setSelectedState("all");
              setSelectedEntity(0);
              setSelectedProvider(0);
              setSelectedMode("all");
              resetSearchParams();
            }}
          >
            Reset Filters
          </Button>

          {excelPermissions.canDownloadExcel && (
            <Button
              variant="outline"
              onClick={() => downloadExcel()}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Excel
            </Button>
          )}
        </div>
        <div className="flex items-center space-x-2 dark:text-white">
          <span className="text-sm text-gray-500">Show: </span>
          <Select
            value={limit.toString()}
            onValueChange={value => setLimit(Number(value))}
          >
            <SelectTrigger className="min-w-20">
              <SelectValue placeholder="Select limit" />
            </SelectTrigger>
            <SelectContent className="min-w-20 dark:bg-gray-800 dark:text-white dark:border-gray-700">
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <Pagination
            page={currentPage}
            totalPages={Math.ceil(totalOrders / limit)}
            onPageChange={page => {
              setCurrentPage(page);
              updateSearchParams("page", page.toString());
            }}
          />
        </div>
      </div>

      <div className="rounded-md border dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow className="dark:border-gray-700 dark:bg-gray-800">
              <TableHead className="dark:text-gray-400">Order ID</TableHead>
              <TableHead className="dark:text-gray-400">Placed on</TableHead>
              {(!accessLevel?.access_level?.provider_id ||
                accessLevel.access_level.provider_id.length === 0) && (
                <TableHead className="dark:text-gray-400">Partner</TableHead>
              )}
              <TableHead className="dark:text-gray-400">Status</TableHead>
              <TableHead className="dark:text-gray-400">Last Updated</TableHead>
              <TableHead className="dark:text-gray-400">TAT</TableHead>
              {(!accessLevel?.access_level?.provider_id ||
                accessLevel.access_level.provider_id.length === 0) && (
                <>
                  <TableHead className="dark:text-gray-400">GMV</TableHead>
                  <TableHead className="dark:text-gray-400">NMV</TableHead>
                </>
              )}
              <TableHead className="dark:text-gray-400">Mode</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length > 0 ? (
              orders.map(order => (
                <TableRow
                  key={order.id}
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:border-gray-700 dark:bg-gray-800"
                  onClick={() => {
                    updateSearchParams("orderId", order.id);
                  }}
                >
                  <TableCell className="dark:text-gray-300">
                    {order.invoice_number}
                  </TableCell>
                  <TableCell className="dark:text-gray-300">
                    {new Date(order.created_at ?? "").toLocaleDateString()}
                  </TableCell>
                  {(!accessLevel?.access_level?.provider_id ||
                    accessLevel.access_level.provider_id.length === 0) && (
                    <TableCell className="dark:text-gray-300">
                      {order.entity_name}
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge className={`${statusColors[order.status]}`}>
                      {order.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="dark:text-gray-300">
                    {formatDate(new Date(order.updated_at))}
                  </TableCell>
                  <TableCell className="dark:text-gray-300">
                    {order.tat ? calculateTAT(Number(order.tat)) : "N/A"}
                  </TableCell>
                  {(!accessLevel?.access_level?.provider_id ||
                    accessLevel.access_level.provider_id.length === 0) && (
                    <>
                      <TableCell className="dark:text-gray-300">
                        {order.gmv}
                      </TableCell>
                      <TableCell className="dark:text-gray-300">
                        {order.net_amount}
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    {(() => {
                      const displayMode = (order.api_body?.delivery_mode === "STORE_PICKUP" || order.mode === "store_pickup")
                        ? "store_pickup"
                        : order.mode;
                      const iconData = modeIcons[displayMode] || modeIcons.sdd;
                      return (
                        <img
                          src={iconData.icon}
                          alt={displayMode}
                          className={`${iconData.bgColor} ${iconData.color} p-1`}
                        />
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center dark:text-gray-400"
                >
                  No orders found for the selected filters
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <OrderDrawer
        orderId={selectedOrderId ? Number(selectedOrderId) : null}
        onClose={handleDrawerClose}
      />

    <CommandDialog open={isCommandOpen} onOpenChange={setIsCommandOpen}>
        <CommandInput placeholder="Type a command or search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Quick Actions">
            {COMMANDS.map(command => (
              <CommandItem
                asChild
                key={command.id}
                value={command.id}
                onSelect={() => {
                  command.action(navigate);
                  mixpanelInstance?.track("command_executed", {
                    command_name: command.name,
                    command_shortcut: command.shortcut.join(" + "),
                  });
                  setIsCommandOpen(false);
                }}
                className="cursor-pointer"
              >
                <button
                  className="flex justify-between w-full text-left w-full px-2 py-3 aria-selected:bg-gray-100 dark:aria-selected:bg-gray-700"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      command.action(navigate);
                      mixpanelInstance?.track("command_executed", {
                        command_name: command.name,
                        command_shortcut: command.shortcut.join(" + "),
                      });
                      setIsCommandOpen(false);
                    }
                  }}
                >
                  <span>{command.name}</span>
                  <span className="text-sm text-gray-500">
                    {command.shortcut.join(" + ")}
                  </span>
                </button>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandGroup heading="Pharmacy-specific Orders">
            {dynamicPharmacyCommands.map(command => (
              <CommandItem
                asChild
                key={command.id}
                value={command.id}
                onSelect={(value) => {
                  command.action(navigate);
                  mixpanelInstance?.track("command_executed", {
                    command_name: command.name,
                    command_shortcut: command.shortcut.join(" + "),
                  });
                  setIsCommandOpen(false);
                }}
                className="cursor-pointer"
              >
                <button className="flex justify-between w-full text-left w-full px-2 py-3 aria-selected:bg-gray-100 dark:aria-selected:bg-gray-700">
                  <span>{command.name}</span>
                  <span className="text-sm text-gray-500">
                    {command.shortcut.join(" + ")}
                  </span>
                </button>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandGroup heading="States">
            {availableStates.filter(state => state).map(state => (
              <CommandItem
                asChild
                key={state}
                value={state}
                onSelect={() => {
                  updateSearchParams("state", state);
                  mixpanelInstance?.track("state_filter_applied", {
                    state: state,
                  });
                  setIsCommandOpen(false);
                }}
                className="cursor-pointer"
              >
                <button className="flex justify-between w-full text-left w-full px-2 py-3 aria-selected:bg-gray-100 dark:aria-selected:bg-gray-700">
                  <span>Show {state} Orders</span>
                  <span className="text-sm text-gray-500">
                    {state?.substring(0, 2).toUpperCase() || ""}
                  </span>
                </button>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandGroup heading="Coming Soon">
            {COMING_SOON_COMMANDS.map(command => (
              <CommandItem
                asChild
                key={command.id}
                value={command.id}
                onSelect={(value) => {
                  command.action(navigate);
                  setIsCommandOpen(false);
                }}
                className="cursor-pointer opacity-60"
              >
                <button className="flex justify-between w-full text-left w-full px-2 py-3 aria-selected:bg-gray-100 dark:aria-selected:bg-gray-700">
                  <span>{command.name} - Coming Soon</span>
                  <span className="text-sm text-gray-500">
                    {command.shortcut.join(" + ")}
                  </span>
                </button>
              </CommandItem>
            ))}
          </CommandGroup>

        </CommandList>
      </CommandDialog>


    </div>
  );
};
